import { useEffect } from 'react';
import type { ExportBlockedReason, ExportSaveFailure } from '../shared/export-telemetry';
import { reportEvent } from './telemetry';

/**
 * Export's telemetry from the screen (catalog §5.9): why the export button was shown disabled, and why a save of export
 * setup failed (the empty Needs review list's export scenes, and their export.scene, were removed on 2026-10-31). Names and attributes are code words from shared/export-telemetry.ts; fire-and-forget.
 */

/** How long the button must stay disabled for one reason before it counts as seen (a passing check is not a hurdle). */
export const EXPORT_BLOCKED_SEEN_MS = 2_000;
const blockedSeen = new Set<ExportBlockedReason>();

/**
 * `export.blocked:<reason>` once per session per reason, when Export's action (the switch's Turn on, or Export now)
 * has been shown disabled for that reason for two seconds. A disabled button sends nothing by itself.
 */
export function useExportBlockedReport(reason: ExportBlockedReason | null, jobId: string): void {
  useEffect(() => {
    if (!reason || blockedSeen.has(reason)) return;
    const timer = window.setTimeout(() => {
      blockedSeen.add(reason);
      reportEvent(`export.blocked:${reason}`, { jobId });
    }, EXPORT_BLOCKED_SEEN_MS);
    return () => window.clearTimeout(timer);
  }, [reason, jobId]);
}

/** `export.save-failed:<reason>` with the destination's kind (a folder or Content Central), never its path. */
export function reportExportSaveFailed(
  failure: ExportSaveFailure,
  destinationKind: 'filesystem' | 'content-central',
  jobId: string,
): void {
  reportEvent(`export.save-failed:${failure.reason}`, {
    jobId,
    attrs: { kind: destinationKind === 'content-central' ? 'content-central' : 'folder' },
  });
}
